import React, { useState, useEffect } from 'react';
import { 
  getAllPromotions, 
  createPromotion, 
  updatePromotion, 
  deletePromotion, 
  togglePromotionStatus,
  getPromotionStats 
} from '../api/promotions';
import '../css/promotionsManagement.css';

function PromotionsManagement() {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);
  const [loadingActions, setLoadingActions] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 });

  const [formData, setFormData] = useState({
    id: '',
    title: '',
    message: '',
    images: [],
    url: '',
    isActive: true
  });

  useEffect(() => {
    fetchPromotions();
    fetchStats();
  }, []);

  const fetchPromotions = async () => {
    try {
      setLoading(true);
      const response = await getAllPromotions();
      
      if (response.success) {
        setPromotions(response.data || []);
      } else {
        setError(response.message || 'Failed to fetch promotions');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch promotions');
      console.error('Error fetching promotions:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await getPromotionStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Error fetching promotion stats:', err);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchPromotions();
    await fetchStats();
    setRefreshing(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddImage = () => {
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, '']
    }));
  };

  const handleRemoveImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleImageChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.map((img, i) => i === index ? value : img)
    }));
  };

  const handleAddPromotion = () => {
    setEditingPromotion(null);
    setFormData({
      id: '',
      title: '',
      message: '',
      images: [],
      url: '',
      isActive: true
    });
    setShowAddModal(true);
  };

  const handleEditPromotion = (promotion) => {
    setEditingPromotion(promotion);
    setFormData({
      id: promotion.id,
      title: promotion.title,
      message: promotion.message || '',
      images: promotion.images || [],
      url: promotion.url || '',
      isActive: promotion.isActive
    });
    setShowAddModal(true);
  };

  const handleSubmitPromotion = async (e) => {
    e.preventDefault();
    
    try {
      setLoadingActions(prev => ({ ...prev, submit: true }));
      
      if (editingPromotion) {
        // Update existing promotion
        const response = await updatePromotion(editingPromotion._id, formData);
        if (response.success) {
          await fetchPromotions();
          await fetchStats();
          setShowAddModal(false);
          setError('');
        } else {
          setError(response.message || 'Failed to update promotion');
        }
      } else {
        // Create new promotion
        const response = await createPromotion(formData);
        if (response.success) {
          await fetchPromotions();
          await fetchStats();
          setShowAddModal(false);
          setError('');
        } else {
          setError(response.message || 'Failed to create promotion');
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to save promotion');
      console.error('Error saving promotion:', err);
    } finally {
      setLoadingActions(prev => ({ ...prev, submit: false }));
    }
  };

  const handleToggleStatus = async (promotionId) => {
    try {
      setLoadingActions(prev => ({ ...prev, [promotionId]: true }));
      
      const response = await togglePromotionStatus(promotionId);
      if (response.success) {
        await fetchPromotions();
        await fetchStats();
      } else {
        setError(response.message || 'Failed to toggle promotion status');
      }
    } catch (err) {
      setError(err.message || 'Failed to toggle promotion status');
      console.error('Error toggling promotion status:', err);
    } finally {
      setLoadingActions(prev => ({ ...prev, [promotionId]: false }));
    }
  };

  const handleDeletePromotion = async (promotionId) => {
    if (!window.confirm('Are you sure you want to delete this promotion? This will deactivate it.')) {
      return;
    }

    try {
      setLoadingActions(prev => ({ ...prev, [promotionId]: true }));
      
      const response = await deletePromotion(promotionId);
      if (response.success) {
        await fetchPromotions();
        await fetchStats();
      } else {
        setError(response.message || 'Failed to delete promotion');
      }
    } catch (err) {
      setError(err.message || 'Failed to delete promotion');
      console.error('Error deleting promotion:', err);
    } finally {
      setLoadingActions(prev => ({ ...prev, [promotionId]: false }));
    }
  };

  if (loading && promotions.length === 0) {
    return (
      <div className="promotions-management">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading promotions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="promotions-management">
      <div className="management-header">
        <div className="header-content">
          <h2>Promotions Management</h2>
          <p>Manage promotional content and campaigns</p>
        </div>
        <div className="header-actions">
          <button 
            className="refresh-btn" 
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <i className={`fas fa-sync-alt ${refreshing ? 'spinning' : ''}`}></i>
            Refresh
          </button>
          <button className="add-btn" onClick={handleAddPromotion}>
            <i className="fas fa-plus"></i>
            Add Promotion
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="stats-container">
        <div className="stat-card">
          <div className="stat-icon">
            <i className="fas fa-tags"></i>
          </div>
          <div className="stat-content">
            <h3>{stats.total}</h3>
            <p>Total Promotions</p>
          </div>
        </div>
        <div className="stat-card active">
          <div className="stat-icon">
            <i className="fas fa-check-circle"></i>
          </div>
          <div className="stat-content">
            <h3>{stats.active}</h3>
            <p>Active Promotions</p>
          </div>
        </div>
        <div className="stat-card inactive">
          <div className="stat-icon">
            <i className="fas fa-pause-circle"></i>
          </div>
          <div className="stat-content">
            <h3>{stats.inactive}</h3>
            <p>Inactive Promotions</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <i className="fas fa-exclamation-triangle"></i>
          {error}
          <button onClick={() => setError('')} className="error-close">
            <i className="fas fa-times"></i>
          </button>
        </div>
      )}

      {/* Promotions List */}
      <div className="promotions-list">
        {promotions.length === 0 ? (
          <div className="no-promotions">
            <div className="no-data-icon">
              <i className="fas fa-tags"></i>
            </div>
            <h3>No Promotions Found</h3>
            <p>Get started by creating your first promotion campaign.</p>
          </div>
        ) : (
          <div className="promotions-grid">
            {promotions.map((promotion) => (
              <div key={promotion._id} className={`promotion-card ${!promotion.isActive ? 'inactive' : ''}`}>
                <div className="promotion-header">
                  <div className="promotion-status">
                    <span className={`status-badge ${promotion.isActive ? 'active' : 'inactive'}`}>
                      {promotion.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="promotion-actions">
                    <button
                      className="action-btn edit"
                      onClick={() => handleEditPromotion(promotion)}
                      title="Edit promotion"
                      disabled={loadingActions[promotion._id]}
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      className={`action-btn toggle ${promotion.isActive ? 'deactivate' : 'activate'}`}
                      onClick={() => handleToggleStatus(promotion._id)}
                      title={promotion.isActive ? 'Deactivate' : 'Activate'}
                      disabled={loadingActions[promotion._id]}
                    >
                      <i className={`fas ${promotion.isActive ? 'fa-pause' : 'fa-play'}`}></i>
                    </button>
                    <button
                      className="action-btn delete"
                      onClick={() => handleDeletePromotion(promotion._id)}
                      title="Delete promotion"
                      disabled={loadingActions[promotion._id]}
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </div>

                <div className="promotion-content">
                  <div className="promotion-id">
                    <strong>ID:</strong> {promotion.id}
                  </div>
                  <h3 className="promotion-title">{promotion.title}</h3>
                  {promotion.message && (
                    <p className="promotion-message">{promotion.message}</p>
                  )}
                  
                  {promotion.images && promotion.images.length > 0 && (
                    <div className="promotion-images">
                      <strong>Images ({promotion.images.length}):</strong>
                      <div className="image-urls">
                        {promotion.images.map((image, index) => (
                          <span key={index} className="image-url" title={image}>
                            {image.length > 30 ? `${image.substring(0, 30)}...` : image}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {promotion.url && (
                    <div className="promotion-url">
                      <strong>URL:</strong> 
                      <a href={promotion.url} target="_blank" rel="noopener noreferrer" className="external-link">
                        {promotion.url.length > 40 ? `${promotion.url.substring(0, 40)}...` : promotion.url}
                        <i className="fas fa-external-link-alt"></i>
                      </a>
                    </div>
                  )}

                  <div className="promotion-meta">
                    <small>
                      Created: {new Date(promotion.createdAt).toLocaleDateString()}
                    </small>
                    {promotion.updatedAt !== promotion.createdAt && (
                      <small>
                        Updated: {new Date(promotion.updatedAt).toLocaleDateString()}
                      </small>
                    )}
                  </div>
                </div>

                {loadingActions[promotion._id] && (
                  <div className="loading-overlay">
                    <div className="loading-spinner"></div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingPromotion ? 'Edit Promotion' : 'Add New Promotion'}</h3>
              <button 
                className="close-btn" 
                onClick={() => setShowAddModal(false)}
                disabled={loadingActions.submit}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <form onSubmit={handleSubmitPromotion} className="promotion-form">
              <div className="form-group">
                <label htmlFor="id">Promotion ID *</label>
                <input
                  type="text"
                  id="id"
                  name="id"
                  value={formData.id}
                  onChange={handleInputChange}
                  required
                  disabled={!!editingPromotion}
                  placeholder="e.g., summer-2024, holiday-special"
                />
                {editingPromotion && (
                  <small className="form-help">ID cannot be changed after creation</small>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="title">Title *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  placeholder="Promotion title"
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  placeholder="Promotion message or description"
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>Images</label>
                <div className="images-input-container">
                  {formData.images.map((image, index) => (
                    <div key={index} className="image-input-row">
                      <input
                        type="url"
                        value={image}
                        onChange={(e) => handleImageChange(index, e.target.value)}
                        placeholder="Image URL"
                        className="image-url-input"
                      />
                      <button
                        type="button"
                        className="remove-image-btn"
                        onClick={() => handleRemoveImage(index)}
                        title="Remove image"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="add-image-btn"
                    onClick={handleAddImage}
                  >
                    <i className="fas fa-plus"></i>
                    Add Image URL
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="url">URL</label>
                <input
                  type="url"
                  id="url"
                  name="url"
                  value={formData.url}
                  onChange={handleInputChange}
                  placeholder="https://example.com"
                />
              </div>

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                  <span className="checkbox-text">Active promotion</span>
                </label>
              </div>

              <div className="modal-actions">
                <button 
                  type="button" 
                  className="cancel-btn"
                  onClick={() => setShowAddModal(false)}
                  disabled={loadingActions.submit}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="submit-btn"
                  disabled={loadingActions.submit}
                >
                  {loadingActions.submit ? (
                    <>
                      <div className="loading-spinner small"></div>
                      {editingPromotion ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save"></i>
                      {editingPromotion ? 'Update Promotion' : 'Create Promotion'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default PromotionsManagement;